import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  getMonth,
  getDaysInMonth,
  getYear,
  getDay,
  isWeekend,
  startOfDay,
  startOfMonth,
  startOfToday,
  isWithinInterval,
  parseISO,
  addDays,
  isBefore
} from 'date-fns';

import {
  CircularProgress,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Button,
  IconButton,
  Divider,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import i18n from '../../i18n';

import AvailabilityBoxControl from './AvailabilityBoxControl';
import AvailabilityRequestModal from './AvailabilityRequestModal';

import {
  AD_LABEL,
  AM_LABEL,
  PM_LABEL,
  NA_LABEL,
  days,
  months,
} from '../../constants/general';

import {
  getFirstOfNextMonth,
  getFirstOfLastMonth,
  getLocalDateFromDateString,
  formatDateOnlyString,
  formatDateOnlyStringFromLocalTime,
} from '../../helpers/helpers';

import {
  fetchFormattedEmployeeSchedule,
  saveEmployeeAvailability,
} from '../../helpers/externalRequests';

class AvailabilityBox extends Component {
  constructor(props) {
    super(props);
    const currentDate = new Date();
    const today = formatDateOnlyStringFromLocalTime(currentDate);
    const firstOfMonth = formatDateOnlyString(startOfMonth(currentDate));
    // 0 based integer representation of month (January = 0)
    const selectedMonth = getMonth(currentDate);
    const selectedYear = getYear(currentDate);
    const firstDayOfWeek = (getDay(startOfMonth(currentDate)) + 6) % 7; // Convert to Monday-based week (Monday = 0, Sunday = 6)
    const weeksToFetch = Math.ceil((getDaysInMonth(currentDate) + firstDayOfWeek) / 7);

    this.state = {
      availability: [],
      isFetchingData: true,
      today,
      firstOfMonth,
      selectedMonth,
      selectedYear,
      weeksToFetch,
      showAvailabilityModal: false,
    };
  }

  componentDidMount = () => {
    const { firstOfMonth, weeksToFetch } = this.state;
    this.fetchData(firstOfMonth, weeksToFetch);
  };

  handleClick = (event) => {
    event.persist();
    const { id } = event.currentTarget;
    const { displaySnackbar } = this.props;
    const { availability } = this.state;

    // id format: 'availableAD-${date}'
    const date = id.substr(12);
    // if employee is trying to modify past availability
    if (this.checkPastDate(date)) {
      displaySnackbar(`${i18n.t('availability:pastAvailability')}`);
      return;
    }
    // Will return AD, AM or PM
    const granularity = id.substr(9, 2);
    const employeeAvailability = availability.find(
      (availablity) => availablity.date === date)
    .availabilityType;
    // if employee is trying to mark unavailable within 7 days
    if (
      (granularity === employeeAvailability || employeeAvailability === AD_LABEL) 
      && this.checkDateInterval(date)
    ) {
      displaySnackbar(
        `${i18n.t('availability:cannotChangeAvailability')}`
      );
    } else {
      switch (granularity) {
        case AM_LABEL:
          this.setState((prevState) => {
            const newAvailability = prevState.availability;
            const index = prevState.availability.findIndex(
              (prevAvailability) => prevAvailability.date === date
            );
            newAvailability[index] = {
              ...newAvailability[index],
              availableAM: !prevState.availability[index].availableAM,
              /**  we want to display a full day box if the availability types for AM and PM match
               *   but not if the employee is booked
               */
              splitView:
                prevState.availability[index].isBookedPM ||
                prevState.availability[index].availableAM ===
                  prevState.availability[index].availablePM,
            };
            return {
              availability: newAvailability,
            };
          });
          break;
        case PM_LABEL:
          this.setState((prevState) => {
            const newAvailability = prevState.availability;
            const index = prevState.availability.findIndex(
              (prevAvailability) => prevAvailability.date === date
            );
            newAvailability[index] = {
              ...newAvailability[index],
              availablePM: !prevState.availability[index].availablePM,
              splitView:
                prevState.availability[index].isBookedAM ||
                prevState.availability[index].availableAM ===
                  prevState.availability[index].availablePM,
            };
            return {
              availability: newAvailability,
            };
          });
          break;
        case AD_LABEL:
          this.setState((prevState) => {
            const newAvailability = prevState.availability;
            const index = prevState.availability.findIndex(
              (prevAvailability) => prevAvailability.date === date
            );
            newAvailability[index] = {
              ...newAvailability[index],
              availableAM: !prevState.availability[index].availableAM,
              availablePM: !prevState.availability[index].availablePM,
            };
            return {
              availability: newAvailability,
            };
          });
          break;
        default:
      }
    }
  };

  handleSetAllAvailable = () => {
    const { includeWeekend } = this.props;

    this.setState((prevState) => {
      const newAvailability = prevState.availability;
      const availability = newAvailability.map((element) => {
        const {
          bookedLocationAM,
          bookedLocationPM,
          date,
          isBookedAM,
          isBookedPM,
        } = element;
        if (
          (!includeWeekend && isWeekend(getLocalDateFromDateString(date))) ||
          this.checkPastDate(date)
        ) {
          return element;
        }
        const bookedDifferentLocation =
          isBookedAM && bookedLocationPM !== bookedLocationAM;
        const availableAM = !isBookedAM;
        const availablePM = !isBookedPM;
        const splitView =
          (!(availableAM === availablePM && !isBookedAM && !isBookedPM) &&
            !(isBookedAM === isBookedPM)) ||
          bookedDifferentLocation;
        return {
          ...element,
          availableAM,
          availablePM,
          splitView,
        };
      });
      return {
        availability,
      };
    });
  };

  checkDateInterval = (date) => {
    const { allowLastMinuteEdit } = this.props;
    if (allowLastMinuteEdit) {
      return false;
    }

    const today = startOfToday();
    const aWeekFromToday = addDays(today, 7);

    return isWithinInterval(parseISO(date), {
      start: today,
      end: aWeekFromToday,
    });
  };

  checkPastDate = (date, today = startOfDay(new Date())) => {
    return isBefore(parseISO(date), today);
  };

  displayUnauthorized = () => {
    const { displaySnackbar } = this.props;
    displaySnackbar(`${i18n.t('authorization:notAuthorized')}`)
  }

  handleSetAllUnavailable = () => {
    const { includeWeekend } = this.props;

    this.setState((prevState) => {
      const { availability: stateAvailability } = this.state;
      const newAvailability = prevState.availability;
      const availability = newAvailability.map((element) => {
        const {
          bookedLocationAM,
          bookedLocationPM,
          date,
          isBookedAM,
          isBookedPM,
        } = element;
        if (
          (!includeWeekend && isWeekend(getLocalDateFromDateString(date))) ||
          (this.checkDateInterval(date) &&
            !stateAvailability.find(
              (availablity) =>
                availablity.date === date &&
                availablity.availabilityType === NA_LABEL
            )) ||
          this.checkPastDate(date)
        ) {
          return element;
        }
        const bookedDifferentLocation =
          isBookedAM && bookedLocationPM !== bookedLocationAM;
        return {
          ...element,
          availableAM: false,
          availablePM: false,
          splitView: isBookedAM !== isBookedPM || bookedDifferentLocation,
        };
      });
      return {
        availability,
      };
    });
  };

  getBooking = (bookingArray, type) => {
    let booked = false;
    bookingArray.forEach((booking) => {
      if (booking.bookingType === AD_LABEL || booking.bookingType === type) {
        booked = true;
      }
    });
    return booked;
  };

  getDeclined = (bookingArray, type) => {
    let declined = false;
    bookingArray.forEach((booking) => {
      if (booking.bookingType === AD_LABEL || booking.bookingType === type) {
        declined = booking.declined;
      }
    });
    return declined;
  }

  getBookedLocation = (bookingArray, type) => {
    let location = null;
    bookingArray.forEach((booking) => {
      if (booking.bookingType === AD_LABEL || booking.bookingType === type) {
        location = booking.bookedLocation;
      }
    });
    return location;
  };

  mapFormattedDates = (element) => {
    const isBookedAM = element.booked
      ? this.getBooking(element.booked, AM_LABEL)
      : false;
    const isBookedPM = element.booked
      ? this.getBooking(element.booked, PM_LABEL)
      : false;
    const declinedAM = element.booked
      ? this.getDeclined(element.booked, AM_LABEL)
      : false;
    const declinedPM = element.booked
      ? this.getDeclined(element.booked, PM_LABEL)
      : false;
    const bookedLocationAM = isBookedAM
      ? this.getBookedLocation(element.booked, AM_LABEL)
      : null;
    const bookedLocationPM = isBookedPM
      ? this.getBookedLocation(element.booked, PM_LABEL)
      : null;
    return {
      ...element,
      availableAM:
        element.availabilityType === AM_LABEL || element.availabilityType === AD_LABEL,
      availablePM:
        element.availabilityType === PM_LABEL || element.availabilityType === AD_LABEL,
      isBookedAM,
      isBookedPM,
      declinedAM,
      declinedPM,
      bookedLocationAM,
      bookedLocationPM,
      splitView:
        bookedLocationAM !== bookedLocationPM ||
        (element.availabilityType !== AD_LABEL &&
          element.availabilityType !== NA_LABEL),
      utcDate: new Date(element.date),
    };
  };

  fetchData = async (firstOfMonth, weeksToFetch) => {
    const { studioEmployeeId, displaySnackbar } = this.props;
    try {
      const response =  await fetchFormattedEmployeeSchedule(studioEmployeeId, firstOfMonth, weeksToFetch);
      const availability = response.data.map(this.mapFormattedDates);
      this.setState({
        availability,
        isFetchingData: false,
      });
    } catch (error) {
      displaySnackbar(`${i18n.t('availability:fetchError')}: ${error}`);
    }
  };

  getAvailabilityType = (availableAM, availablePM) => {
    if (availableAM === availablePM) {
      return availableAM ? AD_LABEL : NA_LABEL;
    }
    return availableAM ? AM_LABEL : PM_LABEL;
  };

  handlePreviousMonth = () => {
    const { firstOfMonth } = this.state;
    const firstOfLastMonth = getFirstOfLastMonth(firstOfMonth);
    const selectedYear = firstOfLastMonth.substr(0, 4);
    const selectedMonth = parseInt(firstOfLastMonth.substr(5, 2), 10) - 1;
    const selectedDay = parseInt(firstOfLastMonth.substr(8, 2), 10);
    const monthDate = new Date(selectedYear, selectedMonth, selectedDay);
    const firstDayOfWeek = (getDay(startOfMonth(monthDate)) + 6) % 7; // Convert to Monday-based week (Monday = 0, Sunday = 6)
    const weeksToFetch = Math.ceil(
      (getDaysInMonth(monthDate) + firstDayOfWeek) / 7,
    );

    this.setState(
      {
        isFetchingData: true,
        selectedYear,
        selectedMonth,
        weeksToFetch,
        firstOfMonth: firstOfLastMonth,
      },
      () => this.fetchData(firstOfLastMonth, weeksToFetch),
    );
  };

  toggleAvailabilityModal = () => {
    this.setState((prevState) => {
      const showAvailabilityModal = !prevState.showAvailabilityModal;
      return { showAvailabilityModal };
    });
  };

  handleNextMonth = () => {
    const { firstOfMonth } = this.state;
    const firstOfNextMonth = getFirstOfNextMonth(firstOfMonth);
    const selectedYear = firstOfNextMonth.substr(0, 4);
    const selectedMonth = parseInt(firstOfNextMonth.substr(5, 2), 10) - 1;
    const selectedDay = parseInt(firstOfNextMonth.substr(8, 2), 10);
    const monthDate = new Date(selectedYear, selectedMonth, selectedDay);
    const firstDayOfWeek = (getDay(startOfMonth(monthDate)) + 6) % 7; // Convert to Monday-based week (Monday = 0, Sunday = 6)
    const weeksToFetch = Math.ceil(
      (getDaysInMonth(monthDate) + firstDayOfWeek) / 7,
    );

    this.setState(
      {
        isFetchingData: true,
        selectedYear,
        selectedMonth,
        weeksToFetch,
        firstOfMonth: firstOfNextMonth,
      },
      () => this.fetchData(firstOfNextMonth, weeksToFetch),
    );
  };

  handleSave = () => {
    const { availability } = this.state;
    const { studioEmployeeId, displaySnackbar } = this.props;
    const availabilities = [];
    for (let i = 0; i < availability.length; i++) {
      availabilities.push({
        date: availability[i].date,
        availabilityType: this.getAvailabilityType(
          availability[i].availableAM,
          availability[i].availablePM
        ),
      });
    }
    const payload = {
      availability: availabilities,
    };

    saveEmployeeAvailability(studioEmployeeId, payload)
      .then((response) => {
        const { status } = response;
        if (status === 200) {
          displaySnackbar(i18n.t('availability:availabilitySaved'));
        } else {
          displaySnackbar(`${i18n.t('availability:saveError')}: ${status}`);
        }
      })
      .catch((error) => {
        displaySnackbar(`${i18n.t('availability:saveError')}: ${error}`);
      });
  };

  handleGranularity = (date) => {
    this.setState((prevState) => {
      const newAvailability = prevState.availability;
      const index = prevState.availability.findIndex(
        (availability) => availability.date === date
      );
      newAvailability[index] = {
        ...newAvailability[index],
        splitView: true,
      };

      return {
        availability: newAvailability,
      };
    });
  };

  mapDailyAvailability = (element, canEdit) => {
    const {
      availableAM,
      availablePM,
      bookedLocationAM,
      bookedLocationPM,
      isBookedAM,
      isBookedPM,
      declinedAM,
      declinedPM,
      date,
    } = element;

    const bookedAllDay =
      isBookedAM && isBookedPM && bookedLocationAM === bookedLocationPM;

    const declinedAllDay =
      declinedAM && declinedPM;

    if (!element.splitView && (availableAM === availablePM || bookedAllDay)) {
      // render one box
      let className;
      let displayText;
      if (bookedAllDay) {
        className = declinedAllDay ? 'availabilityBox declined' : 'ssenseWarning';
        displayText = declinedAllDay ? i18n.t('availability:declined') : i18n.t('availability:booked');
      } else {
        className = availableAM ? ' ssensePrimary' : ' ssenseDefault';
        displayText = availableAM
          ? i18n.t('availability:available')
          : i18n.t('availability:notAvailable');
      }

      return (
        <TableCell key={date} align="center">
          {canEdit ? (
            <Button
              id={`availableAD-${date}`}
              name={`ALL-DAY-${date}`}
              variant="contained"
              className={`availabilityBox availabilityButtonAllDay ${className}`}
              onClick={isBookedAM ? undefined : this.handleClick}
            >
              {displayText}
            </Button>
          ) : (
            <Button
              id={`availableAD-${date}`}
              name={`ALL-DAY-${date}`}
              variant="contained"
              className={`availabilityBox availabilityButtonAllDay ${className}`}
              onClick={this.displayUnauthorized}
            >
              {displayText}
            </Button>
          ) }
        </TableCell>
      );
    }
    // render both AM and PM
    let amClassName;
    let pmClassName;
    let amDisplayText;
    let pmDisplayText;
    if (isBookedAM) {
      amClassName = declinedAM ? ' availabilityBox declined' : ' ssenseWarning';
      amDisplayText = declinedAM ? i18n.t('availability:declined') : i18n.t('availability:booked');
    } else {
      amClassName = availableAM ? ' ssensePrimary' : ' ssenseDefault';
      amDisplayText = availableAM
        ? i18n.t('availability:available')
        : i18n.t('availability:notAvailable');
    }
    if (isBookedPM) {
      pmClassName = declinedPM ? ' availabilityBox declined' : ' ssenseWarning';
      pmDisplayText = declinedPM ? i18n.t('availability:declined') : i18n.t('availability:booked');
    } else {
      pmClassName = availablePM ? ' ssensePrimary' : ' ssenseDefault';
      pmDisplayText = availablePM
        ? i18n.t('availability:available')
        : i18n.t('availability:notAvailable');
    }
    return (
      <TableCell key={date} align="center">
        <Grid
          container
          direction="column"
          className="availabilityAmPmContainer"
        >

          {canEdit ? (
            <>
              <Button
                id={`availableAM-${date}`}
                name={`AM-${date}`}
                variant="contained"
                className={`availabilityButtonAmPm ${amClassName}`}
                onClick={isBookedAM ? undefined : this.handleClick}
              >
                {amDisplayText}
              </Button>
              <Button
                id={`availablePM-${date}`}
                name={`PM-${date}`}
                variant="contained"
                className={`availabilityButtonAmPm ${pmClassName}`}
                onClick={isBookedPM ? undefined : this.handleClick}
              >
                {pmDisplayText}
              </Button>
            </>
          ) : (
            <>
              <Button
                id={`availableAM-${date}`}
                name={`AM-${date}`}
                variant="contained"
                className={`availabilityButtonAmPm ${amClassName}`}
                onClick={this.displayUnauthorized}
              >
                {amDisplayText}
              </Button>
              <Button
                id={`availablePM-${date}`}
                name={`PM-${date}`}
                variant="contained"
                className={`availabilityButtonAmPm ${pmClassName}`}
                onClick={this.displayUnauthorized}
              >
                {pmDisplayText}
              </Button>
            </>
          )}
        </Grid>
      </TableCell>
    );
  };

  render() {
    const {
      isFetchingData,
      availability,
      today,
      selectedMonth,
      weeksToFetch,
      selectedYear,
      showAvailabilityModal,
    } = this.state;
    const {
      onWeekendClick,
      includeWeekend,
      isFullTime,
      handleRequestAvailability,
      canEdit,
    } = this.props;

    const daysToDisplayPerWeek = includeWeekend ? 7 : 5;
    const daysOfTheWeek = [];
    for (let i = 1; i <= daysToDisplayPerWeek; i++) {
      daysOfTheWeek.push(days[i]);
    }

    const weeklyAvailabilities = [];
    for (let i = 0; i < weeksToFetch; i++) {
      weeklyAvailabilities.push(
        availability.slice(i * 7, i * 7 + daysToDisplayPerWeek)
      );
    }

    if (isFetchingData) {
      return (
        <Grid container spacing={1} justify="center" alignItems="center">
          <div>
            <CircularProgress />
            <span>{i18n.t('loading')}</span>
          </div>
        </Grid>
      );
    }
    return (
      <>
        <Paper className="availabilityBoxContainer">
          <AvailabilityBoxControl
            includeWeekend={includeWeekend}
            isFullTime={isFullTime}
            onWeekendClick={onWeekendClick}
            handleSetAllAvailable={this.handleSetAllAvailable}
            handleSetAllUnavailable={this.handleSetAllUnavailable}
            handleSave={this.handleSave}
            handleNextMonth={this.handleNextMonth}
            handlePreviousMonth={this.handlePreviousMonth}
            toggleAvailabilityModal={this.toggleAvailabilityModal}
            canEdit={canEdit}
          />
          <Divider />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={daysToDisplayPerWeek} align="center">
                  <Typography variant="h2">
                    {`${months[selectedMonth]} ${selectedYear}`}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow key="days">
                {daysOfTheWeek.map((item) => (
                  <TableCell key={`Day-${item}`} align="center">
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyAvailabilities.map((weekAvailability) => {
                return (
                  <React.Fragment
                    key={`Fragment-${weekAvailability[0].date}`}
                  >
                    <TableRow key={`datesRow-${weekAvailability[0].date}`}>
                      {weekAvailability.map((dailyAvailability, innerIndex) => {
                        const className =
                          weekAvailability[innerIndex].date === today
                            ? 'availabilityExpandContentToday'
                            : 'availabilityExpandContent';
                        return (
                          <TableCell
                            key={`Dates-${dailyAvailability.date}`}
                            id={dailyAvailability.date}
                            className="availabilityExpand"
                          >
                            <div className={className}>
                              {dailyAvailability.utcDate.getUTCDate()}
                              <IconButton
                                aria-label="expand"
                                size="medium"
                                onClick={() =>
                                  this.handleGranularity(dailyAvailability.date)
                                }
                              >
                                <ExpandMoreIcon color="secondary" />
                              </IconButton>
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    <TableRow
                      key={`availability-${weekAvailability[0].date}`}
                    >
                      {weekAvailability.map(a => this.mapDailyAvailability(a,canEdit))}
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        {showAvailabilityModal && (
          <AvailabilityRequestModal
            showAvailabilityModal={showAvailabilityModal}
            month={selectedMonth}
            year={parseInt(selectedYear, 10)}
            handleRequestAvailability={handleRequestAvailability}
            toggleAvailabilityModal={this.toggleAvailabilityModal}
          />
        )}
      </>
    );
  }
}

AvailabilityBox.propTypes = {
  studioEmployeeId: PropTypes.number.isRequired,
  isFullTime: PropTypes.bool.isRequired,
  includeWeekend: PropTypes.bool.isRequired,
  onWeekendClick: PropTypes.func.isRequired,
  displaySnackbar: PropTypes.func.isRequired,
  handleRequestAvailability: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  allowLastMinuteEdit: PropTypes.bool.isRequired,
};

export default AvailabilityBox;
